import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { storage, db } from '../firebase/config';

/**
 * Fahrzeug- und Fahrerdokumente (Zulassung, P-Schein, Versicherung …).
 *
 * Die Datei liegt in Firebase Storage, im Firestore-Dokument steht nur ein
 * Eintrag mit `storagePath` — bewusst KEINE Download-URL: `Vehicles` und
 * `users` sind für jeden angemeldeten Nutzer lesbar, und eine Download-URL
 * mit Token umgeht die Storage-Rules. Geöffnet wird über `getDownloadURL`,
 * das die Storage-Rules (nur Portal-Rollen des Unternehmens) prüft.
 *
 * Einträge aus der Zeit vor der Storage-Anbindung sind Attrappen
 * (`https://example.com/...` bzw. reine URL-Strings ohne Datei) — sie lassen
 * sich nur noch entfernen (siehe `isStoredDocument`).
 */

export const MAX_DOCUMENT_SIZE = 20 * 1024 * 1024;
export const ACCEPTED_DOCUMENT_TYPES = 'application/pdf,image/*';

const TARGETS = {
  vehicle: { collection: 'Vehicles', storageRoot: 'vehicleDocuments' },
  driver: { collection: 'users', storageRoot: 'driverDocuments' },
};

const getTarget = (kind) => {
  const target = TARGETS[kind];
  if (!target) throw new Error(`Unbekannte Dokumentart: ${kind}`);
  return target;
};

export const isStoredDocument = (entry) => (
  !!entry && typeof entry === 'object' && typeof entry.storagePath === 'string' && entry.storagePath !== ''
);

export const documentDisplayName = (entry) => {
  if (!entry) return 'Dokument';
  if (typeof entry === 'string') return entry;
  return entry.name || entry.url || 'Dokument';
};

/** Liefert eine Fehlermeldung für ungeeignete Dateien, sonst null. */
export const validateDocumentFile = (file) => {
  const isPdf = file.type === 'application/pdf';
  const isImage = typeof file.type === 'string' && file.type.startsWith('image/');
  if (!isPdf && !isImage) {
    return `„${file.name}" ist kein PDF oder Bild.`;
  }
  if (file.size >= MAX_DOCUMENT_SIZE) {
    return `„${file.name}" ist größer als 20 MB.`;
  }
  return null;
};

const safeFileName = (name) => (
  String(name || 'dokument').replace(/[^A-Za-z0-9._-]+/g, '_').slice(-120)
);

/**
 * Lädt eine Datei hoch und hängt den Eintrag an `documents` des Datensatzes an.
 * Scheitert der Firestore-Eintrag, wird die Datei wieder entfernt, damit keine
 * unreferenzierten Dokumente im Storage liegen bleiben.
 */
export const uploadEntityDocument = async (kind, entityId, file, onProgress = null) => {
  const target = getTarget(kind);
  if (!entityId) throw new Error('Datensatz-ID fehlt');

  const validationError = validateDocumentFile(file);
  if (validationError) throw new Error(validationError);

  const storagePath = `${target.storageRoot}/${entityId}/${Date.now()}_${safeFileName(file.name)}`;
  const fileRef = ref(storage, storagePath);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file, { contentType: file.type });
    task.on(
      'state_changed',
      (snapshot) => {
        if (onProgress && snapshot.totalBytes > 0) {
          onProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        }
      },
      reject,
      resolve
    );
  });

  const entry = {
    name: file.name,
    type: file.type,
    size: file.size,
    uploadDate: new Date().toISOString(),
    storagePath,
  };

  try {
    await updateDoc(doc(db, target.collection, entityId), { documents: arrayUnion(entry) });
  } catch (error) {
    await deleteObject(fileRef).catch(() => {});
    throw error;
  }

  return entry;
};

/**
 * Lädt bei der Neuanlage vorgemerkte Dateien hoch, sobald die ID feststeht.
 * Gibt die Namen der fehlgeschlagenen Dateien zurück.
 */
export const uploadPendingEntityDocuments = async (kind, entityId, files) => {
  const failed = [];
  for (const file of files || []) {
    try {
      await uploadEntityDocument(kind, entityId, file);
    } catch (error) {
      console.error('Vorgemerktes Dokument konnte nicht hochgeladen werden:', error);
      failed.push(file.name);
    }
  }
  return failed;
};

/**
 * Entfernt den Eintrag aus `documents` und danach die Datei aus Storage.
 * Reihenfolge absichtlich so: Ein verwaister Eintrag ohne Datei wäre für den
 * Nutzer sichtbar kaputt, eine verwaiste Datei ohne Eintrag nicht.
 */
export const removeEntityDocument = async (kind, entityId, entry) => {
  const target = getTarget(kind);
  await updateDoc(doc(db, target.collection, entityId), { documents: arrayRemove(entry) });

  if (isStoredDocument(entry)) {
    try {
      await deleteObject(ref(storage, entry.storagePath));
    } catch (error) {
      if (error?.code !== 'storage/object-not-found') throw error;
    }
  }
};

/** Löscht alle Dateien eines Datensatzes (vor dem Löschen des Datensatzes). */
export const deleteAllEntityDocumentFiles = async (entries) => {
  const stored = (Array.isArray(entries) ? entries : []).filter(isStoredDocument);
  await Promise.all(stored.map(entry => (
    deleteObject(ref(storage, entry.storagePath)).catch((error) => {
      if (error?.code !== 'storage/object-not-found') throw error;
    })
  )));
};

export const getEntityDocumentUrl = (entry) => {
  if (!isStoredDocument(entry)) {
    throw new Error('Zu diesem Eintrag wurde keine Datei gespeichert.');
  }
  return getDownloadURL(ref(storage, entry.storagePath));
};
